<template>
  <div class="agenda-view-page">
    <div class="card card-custom gutter-b">
      <div class="card-header border-0 py-5">
        <h3 class="card-title align-items-start flex-column">
          <span class="card-label font-weight-bolder text-dark">Agenda</span>
          <span class="text-muted mt-2 font-weight-bold font-size-sm">
            Visualize horários livres e ocupados da agenda.
          </span>
        </h3>
        <div class="card-toolbar">
          <router-link
            :to="{ name: 'crmAgenda' }"
            class="btn btn-light font-weight-bolder font-size-sm"
          >
            Voltar
          </router-link>
        </div>
      </div>

      <div class="card-body pt-0">
        <div class="agenda-filter-band mb-8">
          <b-row>
            <b-col md="4" lg="4">
              <b-form-group label="Usuário">
                <VueSelect
                  v-if="canManageAgendaUsers"
                  ref="buscarUsuario"
                  label="nome"
                  :filterable="false"
                  :options="usuarios"
                  v-model="usuario"
                  @input="setUsuario"
                  @search="onSearchUsuario"
                  :clearSearchOnSelect="true"
                >
                  <template slot="no-options">
                    Informe o nome do usuário
                  </template>
                  <template slot="option" slot-scope="option">
                    {{ option.nome }}
                  </template>
                  <template slot="selected-option" slot-scope="option">
                    {{ option.nome }}
                  </template>
                </VueSelect>
                <b-form-input
                  v-else
                  :value="usuarioLabel(currentUserAgendaUser)"
                  disabled
                />
              </b-form-group>
            </b-col>
            <b-col md="3" lg="3">
              <b-form-group label="Data">
                <b-form-input type="date" v-model="data" @change="changeDate" />
              </b-form-group>
            </b-col>
            <b-col md="2" lg="2">
              <b-form-group label="Visualização">
                <div class="agenda-view-toggle">
                  <button
                    type="button"
                    class="agenda-view-option"
                    :class="{ active: viewMode === 'day' }"
                    @click="setViewMode('day')"
                  >
                    Diária
                  </button>
                  <button
                    type="button"
                    class="agenda-view-option"
                    :class="{ active: viewMode === 'week' }"
                    @click="setViewMode('week')"
                  >
                    Semanal
                  </button>
                </div>
              </b-form-group>
            </b-col>
            <b-col md="12" lg="12" class="agenda-filter-actions">
              <button
                type="button"
                class="btn btn-light agenda-action-button mr-2"
                v-b-tooltip.hover.bottom="'Hoje'"
                @click="setToday"
              >
                <CalendarToday :size="20" />
              </button>
              <button
                type="button"
                class="btn btn-light agenda-action-button mr-2"
                v-b-tooltip.hover.bottom="'Bloqueio de agenda'"
                @click="openBloqueioModal"
              >
                <CalendarLock :size="20" />
              </button>
              <button
                type="button"
                class="btn btn-light agenda-action-button mr-2"
                v-b-tooltip.hover.bottom="'Configurações'"
                @click="openConfiguracaoModal"
              >
                <Cog :size="20" />
              </button>
              <button
                type="button"
                class="btn btn-light agenda-action-button mr-2"
                v-b-tooltip.hover.bottom="'Gerar link'"
                @click="openLinkModal"
              >
                <LinkVariant :size="20" />
              </button>
              <button
                type="button"
                class="btn btn-light agenda-search-button font-weight-bold"
                :disabled="!canSearchAgenda || loadingHorarios"
                @click="buscarAgendas"
              >
                <Magnify :size="18" class="mr-1" />
                {{ loadingHorarios ? "Buscando..." : "Buscar agendas" }}
              </button>
            </b-col>
          </b-row>
        </div>

        <div v-if="!canShowAgenda" class="agenda-empty">
          <h5 class="font-weight-bolder mb-2">Selecione um usuário</h5>
          <p class="text-muted mb-0">
            Depois da seleção, a agenda aparece com horários livres e ocupados.
          </p>
        </div>

        <b-row v-else>
          <b-col :md="viewMode === 'week' ? 9 : 8">
            <div class="agenda-calendar-header">
              <button
                type="button"
                class="btn btn-light btn-icon"
                @click="moveDate(-navigationStep)"
              >
                <ChevronLeft :size="20" />
              </button>
              <div class="agenda-date-title">
                <h4 class="mb-1">{{ headerTitle }}</h4>
                <div class="agenda-day-meta">
                  <span>Expediente configurado</span>
                  <span>Blocos de 15 min</span>
                  <span>{{ availableSlotsCount }} horários livres</span>
                </div>
              </div>
              <button
                type="button"
                class="btn btn-light btn-icon"
                @click="moveDate(navigationStep)"
              >
                <ChevronRight :size="20" />
              </button>
              <button
                type="button"
                class="btn btn-light-primary font-weight-bold"
                :disabled="searchingNextAvailable"
                @click="goToNextAvailableDate"
              >
                {{
                  searchingNextAvailable ? "Buscando..." : "Próximo disponível"
                }}
              </button>
            </div>

            <div v-if="loadingHorarios" class="agenda-loading">
              <b-spinner small class="mr-2" />
              Carregando agenda...
            </div>
            <div v-else-if="horariosError" class="alert alert-light-danger">
              <div class="d-flex align-items-center justify-content-between">
                <span>{{ horariosError }}</span>
                <button
                  type="button"
                  class="btn btn-sm btn-light-danger font-weight-bold"
                  @click="loadAgenda"
                >
                  Tentar novamente
                </button>
              </div>
            </div>

            <template v-else-if="viewMode === 'day'">
              <div
                v-if="horarios.expediente && horarios.expediente.fechado"
                class="alert alert-light-warning"
              >
                <div class="d-flex align-items-center justify-content-between">
                  <span>Domingo não possui expediente padrão.</span>
                  <button
                    type="button"
                    class="btn btn-sm btn-light-warning font-weight-bold"
                    @click="moveDate(1)"
                  >
                    Próximo dia
                  </button>
                </div>
              </div>
              <div v-else-if="!hasSlots" class="agenda-empty">
                <h5 class="font-weight-bolder mb-2">
                  Nenhum horário foi recebido.
                </h5>
                <p class="text-muted mb-4">
                  Tente recarregar a agenda ou escolha outro dia.
                </p>
                <button
                  type="button"
                  class="btn btn-light-primary font-weight-bold mr-2"
                  @click="loadAgenda"
                >
                  Recarregar
                </button>
                <button
                  type="button"
                  class="btn btn-light font-weight-bold"
                  @click="moveDate(1)"
                >
                  Próximo dia
                </button>
              </div>
              <div v-else>
                <div v-if="!hasAvailableSlots" class="agenda-empty mb-5">
                  <h5 class="font-weight-bolder mb-2">
                    Dia sem horários disponíveis.
                  </h5>
                  <p class="text-muted mb-4">
                    Todos os horários deste usuário estão ocupados.
                  </p>
                  <button
                    type="button"
                    class="btn btn-light-primary font-weight-bold"
                    @click="goToNextAvailableDate"
                  >
                    Buscar próximo disponível
                  </button>
                </div>

                <div
                  v-for="group in slotGroups"
                  :key="group.label"
                  class="agenda-period"
                >
                  <div class="agenda-period-title">
                    <h5>{{ group.label }}</h5>
                    <span>
                      {{ group.available }} livres de {{ group.total }}
                    </span>
                  </div>

                  <div class="agenda-slots">
                    <div
                      v-for="slot in group.slots"
                      :key="slot.inicio"
                      class="agenda-slot"
                      :class="{ unavailable: !slot.disponivel }"
                      :title="slotTitle(slot)"
                    >
                      <ClockOutline :size="16" />
                      <span>{{ slotLabel(slot) }}</span>
                      <small>{{ slotStatusLabel(slot) }}</small>
                      <strong
                        v-if="isBloqueioSlot(slot)"
                        class="agenda-slot-client"
                      >
                        Bloqueado
                      </strong>
                      <strong
                        v-else-if="slot.ocupado"
                        class="agenda-slot-client"
                      >
                        {{ slot.ocupado.cliente || "Cliente" }}
                      </strong>
                      <em v-if="slot.ocupado && !isBloqueioSlot(slot)">
                        {{ ocupadoServico(slot.ocupado) }}
                      </em>
                    </div>
                  </div>
                </div>
              </div>
            </template>

            <template v-else>
              <div v-if="!hasWeekData" class="agenda-empty">
                <h5 class="font-weight-bolder mb-2">
                  Nenhum horário foi recebido.
                </h5>
                <p class="text-muted mb-0">
                  Tente recarregar a agenda semanal ou escolha outra data.
                </p>
              </div>
              <div v-else class="agenda-week-wrapper">
                <div class="agenda-week-grid agenda-week-header">
                  <div class="agenda-week-time-header"></div>
                  <div
                    v-for="day in weekDays"
                    :key="day.date"
                    class="agenda-week-day-header"
                    :class="{
                      closed: isWeekDayClosed(day.date),
                      selected: day.date === selectedDate,
                    }"
                  >
                    <strong>{{ day.label }}</strong>
                    <span>{{ day.shortDate }}</span>
                    <small v-if="isWeekDayClosed(day.date)">Fechado</small>
                  </div>
                </div>

                <div
                  class="agenda-week-grid agenda-week-body"
                  :style="weekGridStyle"
                >
                  <template v-for="(time, rowIndex) in weekTimeRows">
                    <div
                      :key="`time-${time}`"
                      class="agenda-week-time"
                      :style="{ gridRow: rowIndex + 1 }"
                    >
                      {{ time }}
                    </div>
                    <div
                      v-for="(day, dayIndex) in weekDays"
                      :key="`${day.date}-${time}`"
                      class="agenda-week-cell"
                      :class="weekCellClass(day.date, time)"
                      :style="{
                        gridColumn: dayIndex + 2,
                        gridRow: rowIndex + 1,
                      }"
                    ></div>
                  </template>

                  <div
                    v-for="event in weekEvents"
                    :key="event.key"
                    class="agenda-week-event"
                    :class="{
                      'is-short': event.isShort,
                      'is-blocked': event.isBlocked,
                    }"
                    :style="{
                      gridColumn: event.column,
                      gridRow: event.row,
                      borderLeftColor: event.color,
                      backgroundColor: event.background,
                      '--lane-index': event.laneIndex,
                      '--lane-count': event.laneCount,
                    }"
                    :title="event.title"
                  >
                    <template v-if="event.isBlocked">
                      <span>Bloqueado</span>
                    </template>
                    <template v-else-if="event.isShort">
                      <strong>{{ event.shortTime }}</strong>
                      <span>{{ event.cliente }}</span>
                    </template>
                    <template v-else-if="!event.isBlocked">
                      <strong>{{ event.time }}</strong>
                      <span>{{ event.cliente }}</span>
                      <small v-if="event.usuario">{{ event.usuario }}</small>
                      <small>{{ event.servico }}</small>
                    </template>
                  </div>
                </div>
              </div>
            </template>
          </b-col>

          <b-col :md="viewMode === 'week' ? 3 : 4">
            <div class="agenda-panel">
              <h5 class="font-weight-bolder mb-4">{{ summaryTitle }}</h5>
              <p class="mb-2">
                <strong>Usuário:</strong> {{ summaryUserLabel }}
              </p>
              <p class="mb-4">
                <strong>{{ viewMode === "week" ? "Período" : "Data" }}:</strong>
                {{ summaryDateLabel }}
              </p>

              <div class="agenda-summary-grid mb-5">
                <div>
                  <strong>{{ totalSlotsCount }}</strong>
                  <span>Total</span>
                </div>
                <div>
                  <strong>{{ availableSlotsCount }}</strong>
                  <span>Livres</span>
                </div>
                <div>
                  <strong>{{ busySlotsCount }}</strong>
                  <span>Ocupados</span>
                </div>
              </div>

              <div
                v-if="canManageAgendaUsers && viewMode === 'week'"
                class="agenda-user-filter mb-5"
              >
                <div class="agenda-user-filter-header">
                  <h6 class="font-weight-bolder mb-0">Usuários</h6>
                  <small v-if="hasPendingUserSelection">Seleção alterada</small>
                </div>
                <button
                  type="button"
                  class="btn btn-light btn-sm btn-block agenda-select-all"
                  @click="toggleTodosUsuariosAgenda"
                >
                  {{
                    todosUsuariosSelecionados
                      ? "Desmarcar todos"
                      : "Selecionar todos"
                  }}
                </button>
                <label
                  v-for="usuarioItem in usuarios"
                  :key="usuarioItem.id_usuario"
                  class="agenda-user-check"
                >
                  <input
                    type="checkbox"
                    :value="usuarioItem.id_usuario"
                    v-model="selectedUserIds"
                  />
                  <span
                    class="agenda-user-color"
                    :style="{ backgroundColor: usuarioColor(usuarioItem) }"
                  ></span>
                  {{ usuarioLabel(usuarioItem) }}
                </label>
                <button
                  type="button"
                  class="btn btn-light-primary btn-block font-weight-bold mt-4"
                  :disabled="!canSearchAgenda || loadingHorarios"
                  @click="buscarAgendas"
                >
                  <Magnify :size="18" class="mr-1" />
                  {{ loadingHorarios ? "Buscando..." : "Buscar agendas" }}
                </button>
                <small class="text-muted d-block mt-2">
                  Marque os usuários e clique para atualizar a agenda.
                </small>
              </div>

              <template v-if="viewMode === 'day'">
                <h6 class="font-weight-bolder mb-3">{{ busyListTitle }}</h6>
                <div v-if="busyAppointments.length === 0" class="text-muted">
                  Nenhum agendamento ocupando este dia.
                </div>
                <div
                  v-for="ocupado in busyAppointments"
                  :key="`${ocupado.id_agendamento}-${ocupado.data_inicio}`"
                  class="agenda-busy-item"
                >
                  <strong>
                    {{ formatTime(ocupado.data_inicio) }} -
                    {{ formatTime(ocupado.data_fim) }}
                  </strong>
                  <span>{{ ocupado.cliente || "Cliente" }}</span>
                  <small v-if="ocupado.telefone">
                    Telefone: {{ ocupado.telefone }}
                  </small>
                  <small>{{ ocupadoServico(ocupado) }}</small>
                  <small v-if="ocupado.observacoes">
                    {{ ocupado.observacoes }}
                  </small>
                </div>
              </template>
            </div>
          </b-col>
        </b-row>
      </div>
    </div>

    <b-modal
      id="agendaBloqueioModal"
      centered
      title="Bloqueio de agenda"
      @ok="salvarBloqueio"
      ok-title="Bloquear"
      cancel-title="Cancelar"
    >
      <b-form-group label="Início">
        <b-form-input
          type="datetime-local"
          v-model="bloqueioForm.data_inicio"
        />
      </b-form-group>
      <b-form-group label="Fim">
        <b-form-input type="datetime-local" v-model="bloqueioForm.data_fim" />
      </b-form-group>
      <b-form-group label="Motivo">
        <b-form-textarea rows="3" v-model="bloqueioForm.motivo" />
      </b-form-group>
    </b-modal>

    <b-modal
      id="agendaConfiguracaoModal"
      centered
      size="lg"
      title="Configurações da agenda"
      @ok="salvarConfiguracao"
      ok-title="Salvar"
      cancel-title="Cancelar"
    >
      <div v-for="dia in diasSemana" :key="dia.key" class="agenda-config-day">
        <div class="agenda-config-day-header">
          <strong>{{ dia.label }}</strong>
          <button
            type="button"
            class="btn btn-sm btn-light-primary font-weight-bold"
            @click="addExpedienteBloco(dia.key)"
          >
            Adicionar
          </button>
        </div>
        <div
          v-for="(bloco, index) in configForm.expedientes[dia.key]"
          :key="`${dia.key}-${index}`"
          class="agenda-config-block"
        >
          <b-form-input type="time" v-model="bloco.inicio" />
          <b-form-input type="time" v-model="bloco.fim" />
          <button
            type="button"
            class="btn btn-sm btn-light-danger font-weight-bold"
            @click="removeExpedienteBloco(dia.key, index)"
          >
            Remover
          </button>
        </div>
        <small
          v-if="configForm.expedientes[dia.key].length === 0"
          class="text-muted"
        >
          Sem expediente
        </small>
      </div>
    </b-modal>

    <b-modal
      id="agendaLinkModal"
      centered
      size="lg"
      title="Gerar link de agenda"
      @ok="gerarLinkAgenda"
      ok-title="Gerar link"
      cancel-title="Cancelar"
    >
      <b-row>
        <b-col md="6">
          <b-form-group label="Cliente">
            <VueSelect
              label="nome"
              :filterable="false"
              :options="clientes"
              v-model="linkForm.cliente"
              @search="onSearchCliente"
              :clearSearchOnSelect="true"
            >
              <template slot="no-options">Informe o nome do cliente</template>
            </VueSelect>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col md="4">
          <b-form-group label="Data inicial">
            <b-form-input type="date" v-model="linkForm.data_inicio" />
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group label="Data final">
            <b-form-input type="date" v-model="linkForm.data_fim" />
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group label="Duração">
            <b-form-input
              type="number"
              min="1"
              max="1440"
              v-model.number="linkForm.duracao_minutos"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <div v-if="linkGerado" class="alert alert-light-success mb-0">
        <strong>Link gerado:</strong>
        <div class="agenda-link-result">{{ linkGerado }}</div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import moment from "moment";
import _ from "lodash";
import { mapGetters } from "vuex";
import VueSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import { SistemaMixin } from "@/modules/Application";
import ChevronLeft from "vue-material-design-icons/ChevronLeft.vue";
import ChevronRight from "vue-material-design-icons/ChevronRight.vue";
import ClockOutline from "vue-material-design-icons/ClockOutline.vue";
import CalendarLock from "vue-material-design-icons/CalendarLock.vue";
import CalendarToday from "vue-material-design-icons/CalendarToday.vue";
import Cog from "vue-material-design-icons/Cog.vue";
import LinkVariant from "vue-material-design-icons/LinkVariant.vue";
import Magnify from "vue-material-design-icons/Magnify.vue";
import crmService from "../services/crmService";

const SLOT_MINUTES = 15;
const USER_COLORS = [
  "#3699ff",
  "#1bc5bd",
  "#ffa800",
  "#f64e60",
  "#8950fc",
  "#0bb783",
  "#e83e8c",
  "#3f4254",
];

export default {
  name: "app-crm-agendamento-agenda",
  mixins: [SistemaMixin],
  components: {
    VueSelect,
    ChevronLeft,
    ChevronRight,
    ClockOutline,
    CalendarLock,
    CalendarToday,
    Cog,
    LinkVariant,
    Magnify,
  },
  data() {
    return {
      usuario: null,
      usuarios: [],
      crmPermissao: null,
      selectedUserIds: [],
      loadedUserIds: [],
      clientes: [],
      data: moment().format("YYYY-MM-DD"),
      viewMode: "week",
      loadingHorarios: false,
      searchingNextAvailable: false,
      horariosError: "",
      horarios: {
        expediente: null,
        slots: [],
        ocupados: [],
        resumo: null,
      },
      weekHorarios: {},
      bloqueioForm: {
        data_inicio: "",
        data_fim: "",
        motivo: "",
      },
      configForm: {
        expedientes: {
          0: [],
          1: [{ inicio: "08:00", fim: "18:00" }],
          2: [{ inicio: "08:00", fim: "18:00" }],
          3: [{ inicio: "08:00", fim: "18:00" }],
          4: [{ inicio: "08:00", fim: "18:00" }],
          5: [{ inicio: "08:00", fim: "18:00" }],
          6: [{ inicio: "08:00", fim: "18:00" }],
        },
      },
      linkForm: {
        cliente: null,
        data_inicio: moment().format("YYYY-MM-DD"),
        data_fim: moment().format("YYYY-MM-DD"),
        duracao_minutos: 30,
      },
      linkGerado: "",
      diasSemana: [
        { key: "0", label: "Domingo" },
        { key: "1", label: "Segunda-feira" },
        { key: "2", label: "Terça-feira" },
        { key: "3", label: "Quarta-feira" },
        { key: "4", label: "Quinta-feira" },
        { key: "5", label: "Sexta-feira" },
        { key: "6", label: "Sábado" },
      ],
    };
  },
  computed: {
    ...mapGetters(["currentUser"]),
    currentUserAgendaUser() {
      return {
        id_usuario: this.currentUser?.id_usuario,
        nome: this.currentUser?.nome || this.currentUser?.usuario,
        usuario: this.currentUser?.usuario,
      };
    },
    canManageAgendaUsers() {
      const papeis = Array.isArray(this.crmPermissao?.papeis)
        ? this.crmPermissao.papeis
        : [];

      return (
        !!this.currentUser?.admin ||
        !!this.crmPermissao?.gestor ||
        papeis.includes("gestor")
      );
    },
    canSearchAgenda() {
      if (!this.canManageAgendaUsers) return true;
      if (this.viewMode === "week") return this.selectedUserIds.length > 0;
      return !!this.usuario?.id_usuario;
    },
    canShowAgenda() {
      if (this.canManageAgendaUsers && this.viewMode === "week") {
        return this.loadedUserIds.length > 0;
      }
      return this.canSearchAgenda;
    },
    visibleUsuarios() {
      if (!this.canManageAgendaUsers) {
        return [this.currentUserAgendaUser].filter((item) => item.id_usuario);
      }

      if (this.viewMode === "day") {
        return this.usuario?.id_usuario ? [this.usuario] : [];
      }

      return this.usuariosByIds(this.loadedUserIds);
    },
    hasPendingUserSelection() {
      if (!this.canManageAgendaUsers || this.viewMode !== "week") return false;

      return (
        this.normalizedUserIds(this.selectedUserIds).join(",") !==
        this.normalizedUserIds(this.loadedUserIds).join(",")
      );
    },
    todosUsuariosSelecionados() {
      if (!this.usuarios.length) return false;

      return (
        this.normalizedUserIds(this.selectedUserIds).length ===
        this.normalizedUserIds(
          this.usuarios.map((usuario) => usuario.id_usuario)
        ).length
      );
    },
    agendaTargetUser() {
      if (this.canManageAgendaUsers && this.usuario?.id_usuario) {
        return this.usuario;
      }

      return this.visibleUsuarios[0] || this.currentUserAgendaUser;
    },
    navigationStep() {
      return this.viewMode === "week" ? 7 : 1;
    },
    selectedDate() {
      return this.normalizeDate(this.data);
    },
    weekStartDate() {
      return moment(this.selectedDate).startOf("isoWeek");
    },
    weekEndDate() {
      return this.weekStartDate.clone().add(6, "days");
    },
    headerTitle() {
      if (this.viewMode === "week") {
        return `${this.weekStartDate.format(
          "DD/MM/YYYY"
        )} - ${this.weekEndDate.format("DD/MM/YYYY")}`;
      }

      return moment(this.data).format("dddd, DD/MM/YYYY");
    },
    summaryTitle() {
      return this.viewMode === "week" ? "Resumo da semana" : "Resumo do dia";
    },
    summaryDateLabel() {
      return this.viewMode === "week"
        ? this.headerTitle
        : moment(this.selectedDate).format("dddd, DD/MM/YYYY");
    },
    summaryUserLabel() {
      if (this.viewMode === "week" && this.canManageAgendaUsers) {
        return `${this.visibleUsuarios.length} selecionado(s)`;
      }

      return this.usuarioLabel(this.visibleUsuarios[0]);
    },
    busyListTitle() {
      return "Ocupados no dia";
    },
    totalSlotsCount() {
      if (this.viewMode === "week") {
        return this.weekDays.reduce((total, day) => {
          return total + this.weekDayHorarios(day.date).slots.length;
        }, 0);
      }

      return this.horarios.slots.length;
    },
    availableSlotsCount() {
      if (this.viewMode === "week") {
        return this.weekDays.reduce((total, day) => {
          const slots = this.weekDayHorarios(day.date).slots;
          return total + slots.filter((slot) => slot.disponivel).length;
        }, 0);
      }

      return this.horarios.slots.filter((slot) => slot.disponivel).length;
    },
    busySlotsCount() {
      return this.busyAppointments.length;
    },
    busyAppointments() {
      if (this.viewMode === "week") {
        return this.weekDays.flatMap((day) => {
          return this.weekDayHorarios(day.date).ocupados;
        });
      }

      return this.horarios.ocupados;
    },
    hasSlots() {
      return this.horarios.slots.length > 0;
    },
    hasWeekData() {
      return this.weekDays.some((day) => {
        const horarios = this.weekDayHorarios(day.date);
        return horarios.expediente || horarios.slots.length;
      });
    },
    hasAvailableSlots() {
      return this.availableSlotsCount > 0;
    },
    dailyDisplaySlots() {
      const firstSlotByAppointment = {};

      this.horarios.slots.forEach((slot) => {
        if (slot.disponivel || !slot.id_agendamento_ocupado) return;

        if (!firstSlotByAppointment[slot.id_agendamento_ocupado]) {
          firstSlotByAppointment[slot.id_agendamento_ocupado] = slot.inicio;
        }
      });

      return this.horarios.slots
        .map((slot) => {
          if (slot.disponivel) return slot;

          const ocupado = this.findOcupadoBySlot(slot, this.horarios.ocupados);

          if (!ocupado) return slot;

          const firstSlot =
            firstSlotByAppointment[ocupado.id_agendamento] || slot.inicio;

          if (!moment(slot.inicio).isSame(moment(firstSlot), "minute")) {
            return null;
          }

          return {
            ...slot,
            ocupado,
            inicio: ocupado.data_inicio,
            fim: ocupado.data_fim,
          };
        })
        .filter(Boolean);
    },
    slotGroups() {
      const groups = [
        { label: "Manhã", start: "00:00", end: "11:59", slots: [] },
        { label: "Tarde", start: "12:00", end: "16:59", slots: [] },
        { label: "Fim do dia", start: "17:00", end: "23:59", slots: [] },
      ];

      this.dailyDisplaySlots.forEach((slot) => {
        const hora = moment(slot.inicio).format("HH:mm");
        const group = groups.find(
          (item) => hora >= item.start && hora <= item.end
        );

        if (group) {
          group.slots.push(slot);
        }
      });

      return groups
        .filter((group) => group.slots.length)
        .map((group) => ({
          ...group,
          total: group.slots.length,
          available: group.slots.filter((slot) => slot.disponivel).length,
        }));
    },
    weekDays() {
      return Array.from({ length: 7 }, (_, index) => {
        const date = this.weekStartDate.clone().add(index, "days");

        return {
          date: date.format("YYYY-MM-DD"),
          label: date.format("ddd"),
          shortDate: date.format("DD/MM"),
        };
      });
    },
    weekTimeRows() {
      let minMinutes = null;
      let maxMinutes = null;
      const step = this.weekSlotMinutes;

      this.weekDays.forEach((day) => {
        const horarios = this.weekDayHorarios(day.date);
        const ranges = [
          ...horarios.slots.map((slot) => ({
            inicio: slot.inicio,
            fim: slot.fim,
          })),
          ...horarios.ocupados.map((ocupado) => ({
            inicio: ocupado.data_inicio,
            fim: ocupado.data_fim,
          })),
        ];

        ranges.forEach((range) => {
          if (!range.inicio || !range.fim) return;

          const start = this.timeToMinutes(
            moment(range.inicio).format("HH:mm")
          );
          const end = this.timeToMinutes(moment(range.fim).format("HH:mm"));

          minMinutes =
            minMinutes === null ? start : Math.min(minMinutes, start);
          maxMinutes = maxMinutes === null ? end : Math.max(maxMinutes, end);
        });
      });

      if (minMinutes === null || maxMinutes === null) return [];

      minMinutes = Math.floor(minMinutes / step) * step;
      maxMinutes = Math.ceil(maxMinutes / step) * step;

      const rows = [];

      for (let minutes = minMinutes; minutes < maxMinutes; minutes += step) {
        rows.push(this.minutesToTime(minutes));
      }

      return rows;
    },
    weekSlotMinutes() {
      let hasPreciseTime = false;

      this.weekDays.forEach((day) => {
        const horarios = this.weekDayHorarios(day.date);
        const ranges = [
          ...horarios.slots.map((slot) => ({
            inicio: slot.inicio,
            fim: slot.fim,
          })),
          ...horarios.ocupados.map((ocupado) => ({
            inicio: ocupado.data_inicio,
            fim: ocupado.data_fim,
          })),
        ];

        ranges.forEach((range) => {
          if (!range.inicio || !range.fim) return;

          const start = this.timeToMinutes(
            moment(range.inicio).format("HH:mm")
          );
          const end = this.timeToMinutes(moment(range.fim).format("HH:mm"));
          const duration = Math.max(end - start, 1);

          if (
            start % SLOT_MINUTES !== 0 ||
            end % SLOT_MINUTES !== 0 ||
            duration < SLOT_MINUTES
          ) {
            hasPreciseTime = true;
          }
        });
      });

      return hasPreciseTime ? 5 : SLOT_MINUTES;
    },
    weekRowHeight() {
      return this.weekSlotMinutes < SLOT_MINUTES ? 28 : 34;
    },
    weekGridStyle() {
      return {
        gridTemplateRows: `repeat(${this.weekTimeRows.length}, ${this.weekRowHeight}px)`,
      };
    },
    weekEvents() {
      return this.weekDays.flatMap((day, dayIndex) => {
        const horarios = this.weekDayHorarios(day.date);
        if (horarios.expediente?.fechado) return [];

        const events = horarios.ocupados
          .map((ocupado) => {
            const startIndex = this.timeToRowIndex(ocupado.data_inicio);
            const endIndex = this.timeToRowIndex(ocupado.data_fim, "ceil");
            const clampedStartIndex = Math.max(startIndex, 0);
            const clampedEndIndex = Math.min(
              endIndex,
              this.weekTimeRows.length
            );
            const span = Math.max(clampedEndIndex - clampedStartIndex, 1);

            if (
              endIndex <= 0 ||
              clampedStartIndex >= this.weekTimeRows.length
            ) {
              return null;
            }

            const isBlocked = this.isBloqueioOcupado(ocupado);

            return {
              key: `${day.date}-${ocupado.id_usuario}-${
                ocupado.id_agendamento || ocupado.id_agendamento_bloqueio
              }`,
              column: dayIndex + 2,
              row: `${clampedStartIndex + 1} / span ${span}`,
              rowStartIndex: clampedStartIndex,
              rowEndIndex: clampedEndIndex,
              time: `${this.formatTime(
                ocupado.data_inicio
              )} - ${this.formatTime(ocupado.data_fim)}`,
              shortTime: `${this.formatTime(
                ocupado.data_inicio
              )}-${this.formatTime(ocupado.data_fim)}`,
              cliente: isBlocked ? "Bloqueado" : ocupado.cliente || "Cliente",
              usuario:
                ocupado.usuario || this.usuarioLabel(ocupado.usuario_agenda),
              servico: isBlocked ? "" : this.ocupadoServico(ocupado),
              status: this.statusLabel(ocupado.status),
              color: isBlocked
                ? "#b5b5c3"
                : ocupado.cor_usuario || this.usuarioColor(ocupado),
              background: isBlocked
                ? "#f3f6f9"
                : this.usuarioBackground(ocupado),
              isBlocked,
              isShort: span === 1,
              title: isBlocked
                ? `Bloqueado
${this.formatTime(ocupado.data_inicio)} - ${this.formatTime(ocupado.data_fim)}
${ocupado.observacoes || ocupado.motivo || ""}`
                : `${ocupado.cliente || "Cliente"}
${this.formatTime(ocupado.data_inicio)} - ${this.formatTime(ocupado.data_fim)}
${ocupado.usuario || ""}
${this.ocupadoServico(ocupado)}
${this.statusLabel(ocupado.status)}`,
            };
          })
          .filter(Boolean);

        return this.assignEventLanes(events);
      });
    },
  },
  methods: {
    normalizedUserIds(ids = []) {
      return Array.from(
        new Set(
          ids
            .map((id) => Number(id))
            .filter((id) => Number.isInteger(id) && id > 0)
        )
      ).sort((a, b) => a - b);
    },
    usuariosByIds(ids = []) {
      const usuariosPorId = this.usuarios.reduce((carry, usuario) => {
        carry[Number(usuario.id_usuario)] = usuario;
        return carry;
      }, {});
      const currentId = Number(this.currentUserAgendaUser?.id_usuario || 0);

      return this.normalizedUserIds(ids)
        .map((id) => {
          if (usuariosPorId[id]) return usuariosPorId[id];
          if (id === currentId) return this.currentUserAgendaUser;
          return null;
        })
        .filter(Boolean);
    },
    defaultUserIds() {
      const idUsuario = Number(this.currentUserAgendaUser?.id_usuario || 0);
      return idUsuario ? [idUsuario] : [];
    },
    toggleTodosUsuariosAgenda() {
      if (this.todosUsuariosSelecionados) {
        this.selectedUserIds = this.defaultUserIds();
        return;
      }

      this.selectedUserIds = this.normalizedUserIds(
        this.usuarios.map((usuario) => usuario.id_usuario)
      );
    },
    ensureCurrentUserOption() {
      const idUsuario = Number(this.currentUserAgendaUser?.id_usuario || 0);

      if (!idUsuario) return;

      const exists = this.usuarios.some(
        (usuario) => Number(usuario.id_usuario) === idUsuario
      );

      if (!exists) {
        this.usuarios = [this.currentUserAgendaUser, ...this.usuarios];
      }
    },
    setUsuario(usuario) {
      this.usuario = usuario;
      this.loadAgenda();
    },
    setViewMode(mode) {
      if (this.viewMode === mode) return;
      this.viewMode = mode;
      this.loadAgenda();
    },
    changeDate() {
      this.loadAgenda();
    },
    moveDate(days) {
      this.data = moment(this.data).add(days, "days").format("YYYY-MM-DD");
      this.loadAgenda();
    },
    setToday() {
      this.data = moment().format("YYYY-MM-DD");
      this.loadAgenda();
    },
    normalizeDate(value) {
      const date = moment(value, ["YYYY-MM-DD", "DD/MM/YYYY"], true);

      return date.isValid()
        ? date.format("YYYY-MM-DD")
        : moment(value).format("YYYY-MM-DD");
    },
    blankHorarios() {
      return {
        expediente: null,
        slots: [],
        ocupados: [],
        resumo: null,
      };
    },
    applyHorariosResponse(data) {
      this.horarios = {
        expediente: data.expediente || null,
        slots: data.slots || [],
        ocupados: data.ocupados || [],
        resumo: data.resumo || null,
      };
    },
    applyWeekResponse(items) {
      this.weekHorarios = items.reduce((carry, item) => {
        if (!carry[item.date]) {
          carry[item.date] = this.blankHorarios();
        }

        const current = carry[item.date];
        current.expediente = current.expediente || item.data.expediente || null;
        current.slots = current.slots.concat(
          this.decorateSlots(item.data.slots || [], item.usuario)
        );
        current.ocupados = current.ocupados.concat(
          this.decorateOcupados(item.data.ocupados || [], item.usuario)
        );
        current.resumo = item.data.resumo || current.resumo;

        return carry;
      }, {});
    },
    async fetchHorarios(data, usuario = this.visibleUsuarios[0]) {
      const agendaUser = this.canManageAgendaUsers
        ? usuario
        : this.currentUserAgendaUser;

      const response = await this.$http.post("/agendamentos/horarios", {
        data,
        id_usuario: agendaUser?.id_usuario,
        duracao_minutos: SLOT_MINUTES,
      });

      return response.data;
    },
    async loadAgenda() {
      if (this.canManageAgendaUsers && this.viewMode === "week") {
        if (this.loadedUserIds.length === 0 && this.selectedUserIds.length) {
          this.loadedUserIds = this.normalizedUserIds(this.selectedUserIds);
        }

        if (this.loadedUserIds.length === 0 || !this.data) {
          this.horariosError = "";
          this.applyHorariosResponse(this.blankHorarios());
          this.weekHorarios = {};
          return;
        }
      } else if (!this.canSearchAgenda || !this.data) {
        this.horariosError = "";
        this.applyHorariosResponse(this.blankHorarios());
        this.weekHorarios = {};
        return;
      }

      if (this.viewMode === "week") {
        await this.loadSemana();
        return;
      }

      await this.loadHorarios();
    },
    async buscarAgendas() {
      if (this.canManageAgendaUsers && this.viewMode === "week") {
        this.loadedUserIds = this.normalizedUserIds(this.selectedUserIds);

        if (this.loadedUserIds.length === 0) {
          this.$toast.warning("Selecione pelo menos um usuário.");
          return;
        }
      }

      await this.loadAgenda();
    },
    async loadHorarios() {
      this.loadingHorarios = true;
      this.horariosError = "";
      try {
        const data = this.normalizeDate(this.data);
        this.data = data;
        const usuario = this.visibleUsuarios[0];
        const horarios = await this.fetchHorarios(data, usuario);
        this.applyHorariosResponse({
          ...horarios,
          slots: this.decorateSlots(horarios.slots || [], usuario),
          ocupados: this.decorateOcupados(horarios.ocupados || [], usuario),
        });
      } catch (error) {
        this.applyHorariosResponse(this.blankHorarios());
        this.horariosError =
          error?.response?.data?.msg ||
          error?.response?.data?.message ||
          "Não foi possível carregar a agenda.";
      } finally {
        this.loadingHorarios = false;
      }
    },
    async loadSemana() {
      this.loadingHorarios = true;
      this.horariosError = "";
      try {
        const data = this.normalizeDate(this.data);
        this.data = data;
        const usuarios = this.visibleUsuarios;

        if (usuarios.length === 0) {
          this.weekHorarios = {};
          return;
        }

        const responses = await Promise.all(
          this.weekDays.flatMap((day) => {
            return usuarios.map(async (usuario) => ({
              date: day.date,
              usuario,
              data: await this.fetchHorarios(day.date, usuario),
            }));
          })
        );
        this.applyWeekResponse(responses);
      } catch (error) {
        this.weekHorarios = {};
        this.horariosError =
          error?.response?.data?.msg ||
          error?.response?.data?.message ||
          "Não foi possível carregar a agenda semanal.";
      } finally {
        this.loadingHorarios = false;
      }
    },
    async goToNextAvailableDate() {
      if (!this.canSearchAgenda) {
        this.$toast.warning("Selecione o usuário.");
        return;
      }

      this.searchingNextAvailable = true;
      this.loadingHorarios = true;
      this.horariosError = "";

      try {
        const start = moment(this.data);

        for (let day = 1; day <= 30; day += 1) {
          const data = start.clone().add(day, "days").format("YYYY-MM-DD");
          const horarios = await this.fetchHorarios(
            data,
            this.visibleUsuarios[0]
          );
          const hasAvailable = (horarios.slots || []).some(
            (slot) => slot.disponivel
          );

          if (hasAvailable) {
            this.data = data;
            if (this.viewMode === "week") {
              await this.loadSemana();
            } else {
              this.applyHorariosResponse(horarios);
            }
            return;
          }
        }

        this.horariosError =
          "Não encontrei horários livres nos próximos 30 dias.";
      } catch (error) {
        this.horariosError =
          error?.response?.data?.msg ||
          error?.response?.data?.message ||
          "Não foi possível buscar o próximo horário disponível.";
      } finally {
        this.loadingHorarios = false;
        this.searchingNextAvailable = false;
      }
    },
    weekDayHorarios(date) {
      return this.weekHorarios[date] || this.blankHorarios();
    },
    isWeekDayClosed(date) {
      const horarios = this.weekDayHorarios(date);
      return (
        !!horarios.expediente?.fechado &&
        horarios.slots.length === 0 &&
        horarios.ocupados.length === 0
      );
    },
    weekCellClass(date, time) {
      return {
        closed: this.isWeekCellClosed(date, time),
        blocked: this.isWeekCellBlocked(date, time),
        selected: date === this.selectedDate,
      };
    },
    isWeekCellClosed(date, time) {
      if (this.isWeekDayClosed(date)) return true;

      return this.slotsForWeekCell(date, time).length === 0;
    },
    isWeekCellBlocked(date, time) {
      if (this.isWeekCellClosed(date, time)) return false;

      const slots = this.slotsForWeekCell(date, time);

      if (!slots.length) return false;

      const hasAvailable = slots.some((slot) => slot.disponivel);
      const hasBlocked = slots.some((slot) => this.isBloqueioSlot(slot));

      return hasBlocked && !hasAvailable;
    },
    slotsForWeekCell(date, time) {
      const horarios = this.weekDayHorarios(date);
      const start = moment(`${date} ${time}`, "YYYY-MM-DD HH:mm");
      const end = start.clone().add(this.weekSlotMinutes, "minutes");

      return horarios.slots.filter((slot) => {
        return (
          moment(slot.inicio).isBefore(end) && moment(slot.fim).isAfter(start)
        );
      });
    },
    assignEventLanes(events) {
      const sortedEvents = [...events].sort((a, b) => {
        if (a.rowStartIndex !== b.rowStartIndex) {
          return a.rowStartIndex - b.rowStartIndex;
        }

        return a.rowEndIndex - b.rowEndIndex;
      });

      const clusters = [];
      let currentCluster = [];
      let currentClusterEnd = null;

      sortedEvents.forEach((event) => {
        if (
          currentCluster.length === 0 ||
          event.rowStartIndex < currentClusterEnd
        ) {
          currentCluster.push(event);
          currentClusterEnd =
            currentClusterEnd === null
              ? event.rowEndIndex
              : Math.max(currentClusterEnd, event.rowEndIndex);
          return;
        }

        clusters.push(currentCluster);
        currentCluster = [event];
        currentClusterEnd = event.rowEndIndex;
      });

      if (currentCluster.length) {
        clusters.push(currentCluster);
      }

      return clusters.flatMap((cluster) => {
        const lanes = [];
        const assigned = cluster.map((event) => {
          let laneIndex = lanes.findIndex(
            (laneEnd) => laneEnd <= event.rowStartIndex
          );

          if (laneIndex === -1) {
            laneIndex = lanes.length;
            lanes.push(event.rowEndIndex);
          } else {
            lanes[laneIndex] = event.rowEndIndex;
          }

          return {
            ...event,
            laneIndex,
          };
        });

        const laneCount = Math.max(lanes.length, 1);

        return assigned.map((event) => ({
          ...event,
          laneCount,
        }));
      });
    },
    decorateSlots(slots, usuario) {
      return slots.map((slot) => ({
        ...slot,
        id_usuario_agenda: usuario?.id_usuario,
        usuario_agenda: usuario,
      }));
    },
    decorateOcupados(ocupados, usuario) {
      return ocupados.map((ocupado) => ({
        ...ocupado,
        id_usuario: ocupado.id_usuario || usuario?.id_usuario,
        usuario: ocupado.usuario || this.usuarioLabel(usuario),
        usuario_agenda: usuario,
        cor_usuario: this.usuarioColor(usuario || ocupado),
      }));
    },
    usuarioLabel(usuario) {
      return usuario?.nome || usuario?.usuario || "-";
    },
    usuarioColor(usuario) {
      const id = Number(usuario?.id_usuario || 0);
      const index = id
        ? id % USER_COLORS.length
        : Math.abs(this.usuarioLabel(usuario).length) % USER_COLORS.length;

      return USER_COLORS[index];
    },
    usuarioBackground(usuario) {
      const color = this.usuarioColor(usuario);
      return `${color}18`;
    },
    timeToMinutes(time) {
      const [hours, minutes] = String(time)
        .split(":")
        .map((value) => Number(value));

      return hours * 60 + minutes;
    },
    minutesToTime(totalMinutes) {
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;

      return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
        2,
        "0"
      )}`;
    },
    timeToRowIndex(value, rounding = "floor") {
      if (!this.weekTimeRows.length) return -1;

      const firstRowMinutes = this.timeToMinutes(this.weekTimeRows[0]);
      const valueMinutes = this.timeToMinutes(moment(value).format("HH:mm"));
      const rowValue = (valueMinutes - firstRowMinutes) / this.weekSlotMinutes;

      return rounding === "ceil" ? Math.ceil(rowValue) : Math.floor(rowValue);
    },
    isBloqueioSlot(slot) {
      return (
        !!slot.id_bloqueio_ocupado ||
        slot.status === "bloqueio" ||
        String(slot.motivo || "")
          .toLowerCase()
          .includes("bloque")
      );
    },
    isBloqueioOcupado(ocupado) {
      return (
        ocupado?.tipo === "bloqueio" ||
        !!ocupado?.id_agendamento_bloqueio ||
        ocupado?.status === "bloqueio"
      );
    },
    slotLabel(slot) {
      const inicio = moment(slot.inicio).format("HH:mm");
      const fim = slot.fim ? moment(slot.fim).format("HH:mm") : "";

      return fim ? `${inicio} - ${fim}` : inicio;
    },
    slotStatusLabel(slot) {
      if (this.isBloqueioSlot(slot)) return "Bloqueado";
      if (slot.ocupado) return this.statusLabel(slot.ocupado.status);
      return slot.disponivel ? "Livre" : "Horário ocupado";
    },
    slotTitle(slot) {
      if (slot.disponivel) return "Horário livre";
      if (this.isBloqueioSlot(slot)) return "Bloqueado";

      const ocupado = slot.ocupado || this.findOcupadoBySlot(slot);
      const cliente = ocupado?.cliente || "Cliente";
      const servico = ocupado ? this.ocupadoServico(ocupado) : "Agendamento";

      return `${cliente} - ${servico}`;
    },
    findOcupadoBySlot(slot, ocupados = this.horarios.ocupados) {
      if (slot.id_agendamento_ocupado) {
        const ocupado = ocupados.find(
          (item) => item.id_agendamento === slot.id_agendamento_ocupado
        );

        if (ocupado) return ocupado;
      }

      return ocupados.find((item) => {
        return (
          moment(item.data_inicio).isBefore(moment(slot.fim)) &&
          moment(item.data_fim).isAfter(moment(slot.inicio))
        );
      });
    },
    ocupadoServico(ocupado) {
      return (
        ocupado.servico ||
        ocupado.descricao_servico ||
        ocupado.titulo ||
        "Agendamento"
      );
    },
    statusLabel(status) {
      const labels = {
        pendente: "Pendente",
        agendado: "Agendado",
        confirmado: "Confirmado",
        em_atendimento: "Em atendimento",
        concluido: "Concluido",
        cancelado: "Cancelado",
        nao_compareceu: "Nao compareceu",
      };

      return labels[status] || "Horario ocupado";
    },
    formatTime(value) {
      return value ? moment(value).format("HH:mm") : "--:--";
    },
    onSearchUsuario(search, loading) {
      if (search.length) {
        loading(true);
        this.searchUsuario(loading, search, this);
      }
    },
    searchUsuario: _.debounce(async (loading, search, vm) => {
      await vm.loadUsuarios(search);
      loading(false);
    }, 350),
    async loadCrmPermissao() {
      if (this.currentUser?.admin) {
        this.crmPermissao = { gestor: true, papeis: ["gestor"] };
        return;
      }

      try {
        const response = await crmService.permissaoAtribuicoesConfiguracao();
        const body = response?.data || {};
        this.crmPermissao = body.data || body || null;
      } catch (error) {
        this.crmPermissao = null;
      }
    },
    async loadUsuarios(search = "") {
      if (!this.canManageAgendaUsers) {
        this.usuario = this.currentUserAgendaUser;
        return;
      }

      const { data } = await this.$http.post("/usuario/get-usuarios", {
        search,
      });
      this.usuarios = Array.isArray(data) ? data : data.data || [];
      this.ensureCurrentUserOption();

      if (!this.usuario && this.usuarios.length) {
        const [idUsuarioPadrao] = this.defaultUserIds();
        this.usuario =
          this.usuarios.find(
            (usuario) => Number(usuario.id_usuario) === idUsuarioPadrao
          ) || this.usuarios[0];
      }

      if (this.selectedUserIds.length === 0) {
        this.selectedUserIds = this.defaultUserIds();
      }

      if (this.loadedUserIds.length === 0) {
        this.loadedUserIds = this.normalizedUserIds(this.selectedUserIds);
      }
    },
    openBloqueioModal() {
      const baseDate = this.selectedDate;
      this.bloqueioForm = {
        data_inicio: `${baseDate}T08:00`,
        data_fim: `${baseDate}T09:00`,
        motivo: "",
      };
      this.$bvModal.show("agendaBloqueioModal");
    },
    async salvarBloqueio(event) {
      event.preventDefault();

      if (!this.bloqueioForm.motivo || this.bloqueioForm.motivo.length < 3) {
        this.$toast.warning("Informe o motivo do bloqueio.");
        return;
      }

      await this.$http.post("/agendamentos/bloqueios", {
        id_usuario: this.agendaTargetUser?.id_usuario,
        data_inicio: this.datetimeLocalToApi(this.bloqueioForm.data_inicio),
        data_fim: this.datetimeLocalToApi(this.bloqueioForm.data_fim),
        motivo: this.bloqueioForm.motivo,
      });

      this.$bvModal.hide("agendaBloqueioModal");
      this.$toast.success("Agenda bloqueada!");
      this.loadAgenda();
    },
    async openConfiguracaoModal() {
      const { data } = await this.$http.post("/agendamentos/configuracao", {
        id_usuario: this.agendaTargetUser?.id_usuario,
      });

      const configuracao = data.data || data;
      this.configForm = {
        expedientes: this.ensureExpedientes(configuracao.expedientes),
      };
      this.$bvModal.show("agendaConfiguracaoModal");
    },
    async salvarConfiguracao(event) {
      event.preventDefault();

      await this.$http.post("/agendamentos/configuracao/salvar", {
        id_usuario: this.agendaTargetUser?.id_usuario,
        expedientes: this.configForm.expedientes,
      });

      this.$bvModal.hide("agendaConfiguracaoModal");
      this.$toast.success("Configuração salva!");
      this.loadAgenda();
    },
    ensureExpedientes(expedientes = {}) {
      return this.diasSemana.reduce((carry, dia) => {
        carry[dia.key] = (expedientes[dia.key] || []).map((bloco) => ({
          inicio: bloco.inicio,
          fim: bloco.fim,
        }));

        return carry;
      }, {});
    },
    addExpedienteBloco(dia) {
      this.configForm.expedientes[dia].push({
        inicio: "08:00",
        fim: "18:00",
      });
    },
    removeExpedienteBloco(dia, index) {
      this.configForm.expedientes[dia].splice(index, 1);
    },
    openLinkModal() {
      this.linkGerado = "";
      this.linkForm = {
        cliente: null,
        data_inicio: this.selectedDate,
        data_fim: this.selectedDate,
        duracao_minutos: 30,
      };
      this.$bvModal.show("agendaLinkModal");
    },
    async gerarLinkAgenda(event) {
      event.preventDefault();

      const duracao = Number(this.linkForm.duracao_minutos || 0);

      if (!this.linkForm.data_inicio || !this.linkForm.data_fim) {
        this.$toast.warning("Informe o período do link.");
        return;
      }

      if (this.linkForm.data_inicio > this.linkForm.data_fim) {
        this.$toast.warning(
          "A data final deve ser igual ou posterior à inicial."
        );
        return;
      }

      if (!Number.isInteger(duracao) || duracao < 1 || duracao > 1440) {
        this.$toast.warning("Informe uma duração entre 1 minuto e 24 horas.");
        return;
      }

      const { data } = await this.$http.post("/agendamentos/link", {
        id_usuario: this.agendaTargetUser?.id_usuario,
        id_pessoa: this.linkForm.cliente?.id_pessoa || null,
        origem: "crm_agenda",
        duracao_minutos: duracao,
        data_inicio: `${this.linkForm.data_inicio} 00:00:00`,
        data_fim: `${this.linkForm.data_fim} 23:59:59`,
      });

      this.linkGerado = data.data?.url_publica || data.url_publica;
      this.$toast.success("Link gerado!");
    },
    onSearchCliente(search, loading) {
      if (search.length) {
        loading(true);
        this.searchCliente(loading, search, this);
      }
    },
    searchCliente: _.debounce(async (loading, search, vm) => {
      vm.clientes = await vm.getClientes(search);
      loading(false);
    }, 350),
    async handleInitialAction() {
      const acao = this.$route.query.acao;

      if (acao === "bloqueio") {
        this.openBloqueioModal();
      }

      if (acao === "configuracao") {
        await this.openConfiguracaoModal();
      }

      if (acao === "link") {
        this.openLinkModal();
      }
    },
    datetimeLocalToApi(value) {
      return moment(value).format("YYYY-MM-DD HH:mm:ss");
    },
  },
  async created() {
    moment.locale("pt-br");
    this.$store.dispatch("setLoading", false);
    await this.loadCrmPermissao();
    await this.loadUsuarios();
    await this.loadAgenda();
    await this.handleInitialAction();
  },
  beforeDestroy() {
    this.$store.dispatch("setLoading", false);
  },
};
</script>

<style scoped>
.agenda-filter-band {
  border: 1px solid #ebedf3;
  border-radius: 6px;
  padding: 18px 18px 0;
  background: #fafafa;
}

.agenda-filter-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding-top: 0;
  justify-content: flex-end;
}

.agenda-action-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  padding: 0;
  color: #f06f61;
}

.agenda-search-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  padding: 0 18px;
}

.agenda-view-toggle {
  display: inline-flex;
  width: 100%;
  height: 42px;
  padding: 4px;
  border: 1px solid #e4e6ef;
  border-radius: 6px;
  background: #fff;
}

.agenda-view-option {
  flex: 1;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: #7e8299;
  font-weight: 700;
}

.agenda-view-option.active {
  background: #e1f0ff;
  color: #3699ff;
}

.agenda-calendar-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 18px;
  flex-wrap: wrap;
}

.agenda-date-title {
  flex: 1;
  min-width: 260px;
}

.agenda-day-meta {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.agenda-day-meta span {
  border: 1px solid #e4e6ef;
  border-radius: 6px;
  padding: 6px 10px;
  color: #7e8299;
  font-weight: 600;
  background: #fff;
}

.agenda-loading {
  padding: 24px;
  color: #7e8299;
}

.agenda-empty {
  border: 1px dashed #d1d3e0;
  border-radius: 6px;
  padding: 22px;
  background: #fafafa;
}

.agenda-period {
  margin-bottom: 22px;
}

.agenda-period-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.agenda-period-title h5 {
  margin: 0;
  font-weight: 700;
  color: #3f4254;
}

.agenda-period-title span {
  color: #7e8299;
  font-weight: 600;
}

.agenda-slots {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 10px;
}

.agenda-slot {
  min-height: 88px;
  border: 1px solid #e4e6ef;
  border-radius: 6px;
  background: #fff;
  color: #3f4254;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  transition: border-color 0.15s ease, background-color 0.15s ease;
  gap: 4px;
}

.agenda-slot small {
  color: #7e8299;
  font-weight: 600;
}

.agenda-slot em {
  max-width: 100%;
  padding: 0 8px;
  color: #7e8299;
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  text-align: center;
  word-break: break-word;
}

.agenda-slot.unavailable {
  background: #f3f6f9;
  color: #b5b5c3;
}

.agenda-slot.unavailable .agenda-slot-client {
  color: #3f4254;
  font-size: 13px;
  line-height: 1.2;
  max-width: 100%;
  padding: 0 6px;
  text-align: center;
  word-break: break-word;
}

.agenda-week-wrapper {
  overflow-x: auto;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  background: #fff;
}

.agenda-week-grid {
  display: grid;
  grid-template-columns: 72px repeat(7, minmax(170px, 1fr));
  min-width: 1262px;
}

.agenda-week-header {
  position: sticky;
  top: 0;
  z-index: 5;
  border-bottom: 1px solid #ebedf3;
  background: #fafafa;
}

.agenda-week-time-header,
.agenda-week-day-header {
  min-height: 58px;
  border-right: 1px solid #ebedf3;
}

.agenda-week-day-header {
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 8px 10px;
}

.agenda-week-day-header strong {
  color: #3f4254;
  text-transform: capitalize;
}

.agenda-week-day-header span,
.agenda-week-day-header small {
  color: #7e8299;
  font-weight: 600;
}

.agenda-week-day-header.closed {
  background: #f3f6f9;
}

.agenda-week-day-header.selected {
  background: #e1f0ff;
}

.agenda-week-body {
  position: relative;
}

.agenda-week-time {
  padding: 8px 10px 0 0;
  border-right: 1px solid #ebedf3;
  border-bottom: 1px solid #f3f6f9;
  color: #7e8299;
  font-size: 12px;
  font-weight: 700;
  text-align: right;
}

.agenda-week-cell {
  border-right: 1px solid #ebedf3;
  border-bottom: 1px solid #f3f6f9;
  background: #fff;
}

.agenda-week-cell.closed {
  background: #f8f8fb;
}

.agenda-week-cell.blocked {
  background: #f3f6f9;
}

.agenda-week-cell.selected {
  background: #fbfdff;
}

.agenda-week-event {
  z-index: 2;
  min-height: 30px;
  align-self: stretch;
  justify-self: start;
  width: calc((100% - 8px) / var(--lane-count, 1) - 4px);
  margin-top: 2px;
  margin-bottom: 2px;
  margin-left: calc(
    4px + ((100% - 8px) / var(--lane-count, 1)) * var(--lane-index, 0)
  );
  margin-right: 0;
  padding: 6px 8px;
  overflow: hidden;
  border-left: 3px solid #ffa800;
  border-radius: 5px;
  background: #fff8dd;
  color: #3f4254;
  box-shadow: 0 1px 3px rgba(24, 28, 50, 0.08);
}

.agenda-week-event.is-short {
  display: block;
  min-height: 24px;
  padding: 3px 6px;
}

.agenda-week-event.is-blocked {
  display: flex;
  align-items: center;
  justify-content: center;
  border-left-color: #b5b5c3 !important;
  background: #f3f6f9 !important;
  color: #7e8299;
  box-shadow: none;
}

.agenda-week-event strong,
.agenda-week-event span,
.agenda-week-event small {
  display: block;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.agenda-week-event strong {
  font-size: 12px;
}

.agenda-week-event span {
  font-weight: 700;
}

.agenda-week-event small {
  color: #7e8299;
  font-weight: 600;
}

.agenda-config-section,
.agenda-config-day {
  border: 1px solid #ebedf3;
  border-radius: 6px;
  padding: 14px;
  margin-bottom: 14px;
  background: #fafafa;
}

.agenda-config-day-header,
.agenda-config-block {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.agenda-config-day-header {
  justify-content: space-between;
}

.agenda-config-block .form-control {
  max-width: 150px;
}

.agenda-import-box {
  border-top: 1px solid #ebedf3;
  margin-top: 14px;
  padding-top: 14px;
}

.agenda-panel {
  border: 1px solid #ebedf3;
  border-radius: 6px;
  padding: 18px;
  background: #fff;
  position: sticky;
  top: 90px;
}

.agenda-summary-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

.agenda-summary-grid div {
  border: 1px solid #ebedf3;
  border-radius: 6px;
  padding: 10px;
  text-align: center;
  background: #fafafa;
}

.agenda-summary-grid strong,
.agenda-summary-grid span {
  display: block;
}

.agenda-summary-grid strong {
  color: #3f4254;
}

.agenda-summary-grid span {
  color: #7e8299;
  font-size: 12px;
  font-weight: 600;
}

.agenda-user-filter {
  border-top: 1px solid #ebedf3;
  padding-top: 16px;
}

.agenda-user-filter-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 12px;
}

.agenda-user-filter-header small {
  color: #f06f61;
  font-weight: 700;
}

.agenda-select-all {
  margin-bottom: 12px;
}

.agenda-user-check {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  color: #3f4254;
  font-weight: 600;
}

.agenda-user-check input {
  margin: 0;
}

.agenda-user-color {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex: 0 0 12px;
}

.agenda-busy-item {
  border-left: 3px solid #ffa800;
  padding: 8px 0 8px 12px;
  margin-bottom: 10px;
  background: #fff8dd;
}

.agenda-busy-item span,
.agenda-busy-item small {
  display: block;
}
</style>
